// renderEmail — substitutes {{variable}} placeholders in a template using the
// data shape produced by buildEmailContext(). Returns { subject, body, html }.
//
// Usage in production code (called by your developer when sending):
//
//   const ctx = buildEmailContext({ rental, vehicle, customer, model, invoice, damage, companySettings });
//   const { subject, body, html } = renderEmail(template, ctx);
//   mailer.send({ to: customer.email, subject, html });

/**
 * Build the flat key/value context for variable substitution.
 * Pass in whatever you have; missing fields fall through to an empty string.
 */
export function buildEmailContext({
  rental = {},
  vehicle = {},
  customer = {},
  model = {},
  invoice = {},
  damage = {},
  companySettings = {},
} = {}) {
  // Derive sensible display values
  const customerFull = customer.fname && customer.lname
    ? `${customer.fname} ${customer.lname}`.trim()
    : (rental.customer || customer.name || '');
  const customerFirst = customerFull.split(' ')[0] || '';
  const vehicleDisplay = model.name || (vehicle.name?.split('–')[0]?.trim()) || vehicle.name || '';

  return {
    // Customer
    customer_name:    customerFull,
    customer_first:   customerFirst,
    customer_email:   rental.email || customer.email || '',
    customer_phone:   rental.phone || customer.phone || '',
    customer_country: rental.nationality || customer.nationality || '',

    // Booking
    booking_ref:      rental.bookingRef || rental.id || '',
    contract_id:      rental.id || '',
    pickup_date:      rental.startLabel || rental.start?.slice(0, 10) || '',
    pickup_time:      rental.t1 || '',
    return_date:      rental.endLabel || rental.end?.slice(0, 10) || '',
    return_time:      rental.t2 || '',
    pickup_location:  rental.pickupLocName || '',
    return_location:  rental.returnLocName || rental.pickupLocName || '',
    rental_days:      String(rental.calSpan || rental.days || ''),

    // Vehicle
    vehicle_model:    vehicleDisplay,
    vehicle_plate:    vehicle.plate || '',
    vehicle_icon:     model.icon || vehicle.icon || '🚗',

    // Money
    total:            num(rental.agreedPrice ?? invoice.total),
    deposit:          num(rental.deposit),
    paid:             num(invoice.status === 'paid' ? invoice.total : 0),
    amount_due:       num(invoice.status === 'paid' ? 0 : (invoice.total ?? rental.agreedPrice)),
    payment_method:   rental.methodLabel || invoice.methodLabel || labelFor(rental.payMethod),

    // Bank transfer
    bank_name:        companySettings.bankName || '',
    bank_account:     companySettings.bankAccountName || '',
    iban:             companySettings.iban || '',
    swift:            companySettings.swift || '',
    payment_reference: rental.bookingRef || rental.id || '',

    // Company
    company_name:     companySettings.name    || 'Your Company',
    company_phone:    companySettings.phone   || '',
    company_email:    companySettings.email   || '',
    company_address:  companySettings.address || '',
    company_website:  companySettings.website || '',
    company_logo:     companySettings.logoDataUrl
      ? `<img src="${companySettings.logoDataUrl}" alt="${escapeAttr(companySettings.name || '')}" style="max-height:60px;max-width:180px;object-fit:contain;display:block;" />`
      : (companySettings.name || ''),
    // Internal — used by the HTML wrapper to auto-prepend a header (NOT a variable)
    _logoDataUrl:     companySettings.logoDataUrl || '',
    _brandColor:      companySettings.brandColor  || '#1A3A5C',

    // Links (developer will replace these with real URLs from the back end)
    manage_booking_link: rental.bookingRef ? `${baseUrl(companySettings)}/manage/${rental.bookingRef}` : '',
    agreement_link:      rental.id ? `${baseUrl(companySettings)}/agreement/${rental.id}` : '',
    receipt_link:        invoice.num ? `${baseUrl(companySettings)}/receipt/${invoice.num}` : '',

    // Damages
    damage_type:        damage.type        || '',
    damage_location:    damage.location    || '',
    damage_cost:        num(damage.finalCost ?? damage.estimatedCost),
    damage_description: damage.description || '',
  };
}

function num(v) {
  if (v == null || v === '') return '';
  const n = parseFloat(v);
  return Number.isFinite(n) ? n.toFixed(2) : '';
}

function escapeAttr(s) {
  return String(s || '').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function labelFor(method) {
  const map = { cash: 'Cash', stripe: 'Card', bank: 'Bank Transfer', online: 'Online' };
  return map[method] || method || '';
}

function baseUrl(cs) {
  return cs.bookingPageUrl || cs.website || 'https://fleetrent.app';
}

/**
 * Substitute {{variable}} placeholders in subject + body using ctx.
 * Missing variables become empty string (or left as token if strict=true).
 *
 * Logo handling:
 *   - If the body contains {{company_logo}}, it's replaced inline (advanced).
 *   - Otherwise, the logo is auto-prepended at the top of the HTML email
 *     (standard newsletter style). The plain-text body never gets the logo
 *     because it can't render HTML.
 */
export function renderEmail(template, ctx = {}, opts = {}) {
  const { strict = false, autoLogo = true } = opts;
  if (!template) return { subject: '', body: '', html: '' };

  // For plain-text body, we don't want raw HTML from {{company_logo}}; substitute with name
  const ctxPlain = { ...ctx, company_logo: ctx.company_name || '' };

  const replaceWith = (mapping) => (str) =>
    String(str || '').replace(/\{\{([\w_]+)\}\}/g, (match, key) => {
      if (mapping[key] !== undefined && mapping[key] !== null && mapping[key] !== '') return String(mapping[key]);
      return strict ? match : '';
    });

  const subject = replaceWith(ctxPlain)(template.subject);
  const bodyText = replaceWith(ctxPlain)(template.body);             // plain text version
  const bodyHtml = replaceWith(ctx)(template.body);                   // html-allowed version

  // Detect if user already placed the logo manually
  const manualLogo = /\{\{company_logo\}\}/.test(template.body || '');
  const headerHtml = (autoLogo && !manualLogo && ctx._logoDataUrl)
    ? `<div style="text-align:center;padding:20px 0 24px 0;border-bottom:3px solid ${ctx._brandColor || '#1A3A5C'};margin-bottom:24px;">
         <img src="${ctx._logoDataUrl}" alt="${escapeAttr(ctx.company_name || '')}" style="max-height:60px;max-width:180px;object-fit:contain;" />
       </div>`
    : '';

  const html = bodyToHtml(bodyHtml, { headerHtml, brandColor: ctx._brandColor });

  return { subject, body: bodyText, html };
}

function bodyToHtml(text, { headerHtml = '', brandColor = '#1A3A5C' } = {}) {
  if (!text && !headerHtml) return '';
  // Only escape characters that aren't part of {{company_logo}} substitution (already HTML)
  // We handle this by escaping everything, then unescaping known-safe <img> tags from the logo variable.
  // Simpler approach: split by <img ... /> tags to preserve them.
  const parts = String(text).split(/(<img\b[^>]*>)/i);
  const safe = parts
    .map(p => {
      if (/^<img\b/i.test(p)) return p; // preserve logo img tag
      return p.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    })
    .join('');
  // Convert newlines to <br>, double-newline to paragraph break
  const paragraphs = safe
    .split(/\n\n+/)
    .map(para => `<p style="margin:0 0 12px 0;line-height:1.6;">${para.replace(/\n/g, '<br/>')}</p>`)
    .join('');
  return `<div style="font-family:Arial,sans-serif;font-size:14px;color:#333;max-width:600px;margin:0 auto;padding:24px;background:#fff;">
    ${headerHtml}
    ${paragraphs}
  </div>`;
}

/**
 * Sample data for the preview pane in the editor.
 * Lets the operator see how their template will render BEFORE sending.
 */
export function sampleEmailContext(companySettings = {}) {
  return buildEmailContext({
    rental: {
      id: 'CNT-007', bookingRef: 'BK-9421',
      customer: 'Maria Demetriou', email: 'maria@example.com', phone: '+357 99 123 456',
      nationality: 'Cyprus',
      start: '2026-06-14T10:00', end: '2026-06-19T10:00',
      startLabel: '14 Jun 2026', endLabel: '19 Jun 2026',
      t1: '10:00', t2: '10:00',
      pickupLocName: 'Paphos Office', returnLocName: 'Paphos Office',
      calSpan: 5, agreedPrice: 275, deposit: 500,
      methodLabel: 'Bank Transfer', payMethod: 'bank',
    },
    vehicle: { plate: 'ABC-123' },
    model:   { name: 'Toyota Corolla Hybrid', icon: '🚗' },
    invoice: { num: 'PF-0042', total: 275, status: 'pending' },
    damage:  { type: 'Dent', location: 'Front-left door', finalCost: 280, description: 'Small dent found at return.' },
    companySettings,
  });
}
